import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useTranslations } from '@lang';
import { LeftSideBarWrapper } from '@common/components/BookingForms/Modal';
import { Availability } from '@features/Availability';
import { GuestDetails } from '@features/GuestDetails';
import { CustomiseStep as Customise } from '@features/Customise';
import { useRouter } from 'shared-routing';
import { entitySelectors } from '@store/entity/selectors';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { guestSelectors } from '@store/guest/selectors';
import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';
import { Moment } from 'moment';
import { SpaEntityFormStepsProps } from './SpaEntityFormSteps.types';

export const SpaEntityFormSteps: FC<SpaEntityFormStepsProps> = ({
  spaEntity,
  existingGuests,
  maxDaysAdvance,
  bookingStep,
  isExtrasAvailable = false,
  goToNextStep,
  finishWizardAction,
  fetchingDates,
  isSpaEntityFetching,
  SpaEntityFormStepEnum,
  entityBookingRoute,
  entityType,
  entityLabel,
  availableTimeslots,
  fetchSpaEntity,
  language,
  setStartDate,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const property = useSelector(entitySelectors.getProperty) as any;
  const showGenderNeutral = useSelector(entitySelectors.isSpaShowGenderNeutralEnabled) as boolean;
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;
  const guest = useSelector(guestSelectors.getGuest) as any;
  const reservedSpaTimeSlots = useSelector(basketSelectors.getReservedSpaTimeSlots) as string[];

  const STICKY_BAR_ID = 'spa-entity-sticky-bar';
  const { id, minimumGuests, extras, availability, telephoneOnly } = spaEntity || {};

  const handleInvalidConfig = useCallback(() => {
    router.push({
      pathname: entityBookingRoute,
      query: {
        id: id,
        bookingStep: SpaEntityFormStepEnum.AVAILABILITY,
      },
    });
  }, [router, id, entityBookingRoute, SpaEntityFormStepEnum]);

  const timeslots = availableTimeslots || availability;
  const isModalOpen = !!bookingStep;

  const fetchSpaEntityAvailability = (arrive: Moment) => {
    if (fetchSpaEntity) {
      fetchSpaEntity(property.id, id, language, arrive);
    }
  };

  const Gender = {
    M: 'male',
    F: 'female',
  };

  const genderSpecific = spaEntity?.genderSpecific;
  const guests = new Array(minimumGuests).fill({
    firstName: '',
    lastName: '',
    gender: genderSpecific ? Gender[genderSpecific] || '' : '',
  });

  const initialValues = {
    startDate: journeyParams.spaDate || journeyParams.fromDate,
    guests,
  };

  const { setValue, getValues } = useFormContext<SpaEntityFormValues>();
  const hasInitialisedGuests = useRef(false);

  useEffect(() => {
    if (minimumGuests && !hasInitialisedGuests.current) {
      const currentGuests = getValues('guests');
      if (!currentGuests || currentGuests.length === 0) {
        setValue('guests', guests);
        hasInitialisedGuests.current = true;
      }
    }
  }, [minimumGuests]);

  useEffect(() => {
    const startDateValue = journeyParams.spaDate || journeyParams.fromDate;
    if (startDateValue && !getValues('startDate')) {
      setValue('startDate', startDateValue);
    }
  }, [journeyParams.spaDate, journeyParams.fromDate]);

  const isPaidExtras = (extras) => extras?.price?.amount > 0;

  const paidExtras = extras
    ?.filter((extras) => isPaidExtras(extras))
    .sort((a, b) => b.featured - a.featured);
  const freeExtras = extras
    ?.filter((extras) => !isPaidExtras(extras))
    .sort((a, b) => b.featured - a.featured);

  const typeName = t(entityLabel);

  const formSteps = {
    [SpaEntityFormStepEnum.AVAILABILITY]: (
      <LeftSideBarWrapper key={SpaEntityFormStepEnum.AVAILABILITY}>
        <Availability
          stickyBarId={STICKY_BAR_ID}
          isModalOpen={isModalOpen}
          spaEntity={spaEntity as any}
          allTimeslots={(timeslots || []) as any}
          initialValues={{ startDate: initialValues.startDate as any }}
          maxDaysAdvance={maxDaysAdvance}
          property={property}
          entityType={entityType}
          fetchingDates={fetchingDates}
          fetchAvailability={fetchSpaEntityAvailability}
          setStartDate={setStartDate}
          reservedSpaTimeSlots={reservedSpaTimeSlots}
        />
        {!telephoneOnly && (
          <GuestDetails
            guest={guest}
            spaEntity={spaEntity}
            typeName={typeName}
            entityType={entityType}
            stickyBarId={STICKY_BAR_ID}
            onSubmit={isExtrasAvailable ? goToNextStep : finishWizardAction}
            existingGuests={existingGuests}
            handleInvalidConfig={handleInvalidConfig}
            showGenderNeutral={showGenderNeutral}
            isItemFetching={isSpaEntityFetching}
          />
        )}
      </LeftSideBarWrapper>
    ),
    [SpaEntityFormStepEnum.CUSTOMISE]: isExtrasAvailable ? (
      <LeftSideBarWrapper key={SpaEntityFormStepEnum.CUSTOMISE}>
        <Customise
          spaEntity={spaEntity}
          stickyBarId={STICKY_BAR_ID}
          onSubmit={finishWizardAction}
          freeExtras={freeExtras}
          paidExtras={paidExtras}
          handleInvalidConfig={handleInvalidConfig}
        />
      </LeftSideBarWrapper>
    ) : null,
  };

  return (bookingStep && formSteps[bookingStep]) || <></>;
};
